import { Entities } from '../../store';

import { WebsocketProjectEvent } from '../../websocket';

import {
  NavigationProjectFeedApiResponse,
  NavigationProjectFeedItem,
  NavigationProjectFeedItemApiResponse,
  NavigationUserInfo,
  NewsfeedApiResponseEntry,
} from '../navigation.model';
import { NewsfeedApiResponse } from '../newsfeed';

export function transformNavigationNotifications(
  entries: NewsfeedApiResponseEntry[],
): Entities<NewsfeedApiResponseEntry> {
  return entries.reduce((acc: Entities<NewsfeedApiResponseEntry>, entry) => {
    const id = entry.type === 'contestAwardedToEmployer'
      ? entry.data.entry_id
      : entry.type === 'draftContest' ? entry.data.contestId : entry.data.id;
    return {
      ...acc,
      [`${entry.type}-${id}`]: { type: entry.type, data: entry.data },
    };
  }, {});
}

// This takes an API response and transforms it into a User object
export function transformNavigationUserInfo(
  navigation: any,
): NavigationUserInfo {
  return {
    id: navigation.id,
    username: navigation.username,
    publicName: navigation.publicName,
    isFreeTrialEligible: navigation.isFreeTrialEligible,
    isPlusTrialEligible: navigation.isPlusTrialEligible,
    trialPackageId: navigation.trialPackageId,
    isPremium: navigation.isPremium,
    logo: navigation.logo,
    membership: navigation.membership,
    hideMembershipUpgradeButton: navigation.hideMembershipUpgradeButton,
    trialDuration: navigation.trialDuration,
    trialLandingUrl: navigation.trialLandingUrl,
    monthlyTrial: navigation.monthlyTrial,
    canSwitchToUsers: navigation.canSwitchToUsers,
    isNoCommissionEligibleReferrer: navigation.isNoCommissionEligibleReferrer,
  };
}

// This takes an API response and transforms it into a NavigationProjectFeed object
export function transformNavigationProjectFeedItem(
  item: NavigationProjectFeedItemApiResponse,
): Entities<NavigationProjectFeedItem> {
  return {
    [`${item.type}-${item.id}`]: {
      id: item.id,
      appendedDescr: item.appended_descr,
      currency: item.currency,
      currencyCode: item.currencyCode,
      exchangerate: item.exchangerate,
      featured: item.featured,
      freeBidUntil: item.free_bid_until,
      fulltime: item.fulltime,
      // hidebids: item.hidebids,
      imgUrl: item.imgUrl,
      isMinimallyPostedProject: item.isMinimallyPostedProject,
      jobs: item.jobs.map(job => Number(job)),
      jobString: item.jobString,
      linkUrl: item.linkUrl,
      maxbudget: Number(item.maxbudget),
      minbudget: Number(item.minbudget),
      minimallyPostedProjectBudgetSet: item.minimallyPostedProjectBudgetSet,
      nonpublic: item.nonpublic,
      projIsHourly: item.projIsHourly,
      showBudgetMinimallyPostedProject: item.showBudgetMinimallyPostedProject,
      submitDate: item.submitDate,
      text: item.text,
      time: item.time * 1000, // Turns seconds to miliseconds
      title: item.title,
      type: item.type,
      userId: Number(item.userId),
      userName: item.userName,
    },
  };
}

// This takes an API response and transforms it into a NavigationProjectFeed object
export function transformNavigationProjectFeedItems(
  items: NavigationProjectFeedApiResponse,
): Entities<NavigationProjectFeedItem> {
  if (items) {
    return items.reduce(
      (acc, item) => ({
        ...acc,
        ...transformNavigationProjectFeedItem(item),
      }),
      {},
    );
  }
  return {};
}

export function transformNavigationProjectFeedWS(
  item: WebsocketProjectEvent['data'],
): Entities<NavigationProjectFeedItem> {
  return {
    [`project-${item.id}`]: {
      id: item.id,
      appendedDescr: item.appended_descr,
      currency: item.currency,
      currencyCode: item.currencyCode,
      exchangerate: item.exchangerate,
      featured: item.featured,
      freeBidUntil: item.free_bid_until,
      fulltime: item.fulltime,
      imgUrl: item.imgUrl,
      isMinimallyPostedProject: item.isMinimallyPostedProject,
      jobs: item.jobs.map(job => Number(job)),
      jobString: item.jobString,
      linkUrl: item.linkUrl,
      maxbudget: Number(item.maxbudget),
      minbudget: Number(item.minbudget),
      minimallyPostedProjectBudgetSet: item.minimallyPostedProjectBudgetSet,
      nonpublic: item.nonpublic,
      projIsHourly: item.projIsHourly,
      showBudgetMinimallyPostedProject: item.showBudgetMinimallyPostedProject,
      submitDate: item.submitDate,
      text: item.text,
      time: item.time * 1000, // Turns seconds to miliseconds
      title: item.title,
      type: 'project',
      userId: Number(item.userId),
      userName: item.userName,
    },
  };
}

export function transformUserRecentProjectsAndContests(
  item: any,
): Entities<NavigationProjectFeedItem> {
  return item.list.map((listItem: any) => ({
    ...listItem,
    urlPart: listItem.type === 'project'
      ? `/projects/${listItem.urlPart}`
      : listItem.urlPart,
  }));
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/navigation/reducers/transformers.ts